import {
  hasLastMessagePreview,
  isEventChatItem,
} from '@/modules/chat-matrix-fast/constants/chat'
import {
  getMatrixRoomLastMessage,
  isMatrixRoomHistoryForbidden,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { getMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'
import type { ChatContactWithChat } from '@/modules/chat-matrix-fast/types'
import {
  eventsDebugLog,
  summarizeChatLastMessageFieldsForLog,
  summarizeLastMessageForLog,
} from '@/modules/chat-matrix-fast/utils/chatDebugLog'

/** Matrix-комната event-чата: join, timeline, последнее сообщение в SDK. */
export function describeEventChatMatrixRoom(roomId: string | null | undefined) {
  const normalized = roomId?.trim() || null
  if (!normalized) {
    return {
      roomId: null,
      inSdk: false,
      membership: null as string | null,
      timelineEvents: 0,
      lastInTimeline: null,
      historyForbidden: false,
    }
  }

  try {
    const room = getMatrixClient().getRoom(normalized)
    if (!room) {
      return {
        roomId: normalized,
        inSdk: false,
        membership: null as string | null,
        timelineEvents: 0,
        lastInTimeline: null,
        historyForbidden: isMatrixRoomHistoryForbidden(normalized),
      }
    }

    const last = getMatrixRoomLastMessage(room)
    return {
      roomId: normalized,
      inSdk: true,
      membership: room.getMyMembership(),
      timelineEvents: room.getLiveTimeline().getEvents().length,
      lastInTimeline: summarizeLastMessageForLog(last),
      historyForbidden: isMatrixRoomHistoryForbidden(normalized),
    }
  } catch {
    return {
      roomId: normalized,
      inSdk: false,
      membership: null as string | null,
      timelineEvents: 0,
      lastInTimeline: null,
      historyForbidden: false,
    }
  }
}

function summarizeEventChatEntry(chat: IChat) {
  return {
    ...summarizeChatLastMessageFieldsForLog(chat),
    type: chat.type,
    event_url: chat.event_url ?? null,
    unread_count: chat.unread_count ?? 0,
    hasPreview: hasLastMessagePreview(chat.last_message),
    matrix: describeEventChatMatrixRoom(chat.matrix_room_id),
  }
}

/** Один event-чат — только при chatDebug=1 и type=event. */
export function logEventChat(
  stage: string,
  chat: IChat | null | undefined,
  extra?: Record<string, unknown>,
): void {
  if (!chat || !isEventChatItem(chat)) {
    return
  }

  eventsDebugLog(stage, {
    ...summarizeEventChatEntry(chat),
    ...extra,
  })
}

/** Список event-чатов. */
export function logEventChats(
  stage: string,
  chats: IChat[],
  extra?: Record<string, unknown>,
): void {
  const events = chats.filter(isEventChatItem)
  if (!events.length) {
    eventsDebugLog(`${stage} · empty`, extra)
    return
  }

  eventsDebugLog(stage, {
    count: events.length,
    chats: events.map(summarizeEventChatEntry),
    ...extra,
  })
}

/** Что реально уходит в sidebar на вкладке «Чаты задач». */
export function logEventSidebarContacts(
  stage: string,
  contacts: ChatContactWithChat[],
  extra?: Record<string, unknown>,
): void {
  const eventContacts = contacts.filter(contact =>
    isEventChatItem(contact.chat as IChat | undefined),
  )

  if (!eventContacts.length) {
    eventsDebugLog(`${stage} · empty`, extra)
    return
  }

  eventsDebugLog(stage, {
    count: eventContacts.length,
    items: eventContacts.map(contact => ({
      contactId: contact.id,
      fullName: contact.fullName,
      chatId: contact.chat?.id ?? null,
      lastMessage: contact.chat?.lastMessage
        ? {
            message: contact.chat.lastMessage.message?.slice(0, 80) ?? '',
            time: contact.chat.lastMessage.time ?? '',
            senderId: contact.chat.lastMessage.senderId ?? null,
          }
        : null,
      unseenMsgs: contact.chat?.unseenMsgs ?? 0,
    })),
    ...extra,
  })
}
